import { useState, useMemo, useCallback, useEffect } from "react";
import React from "react";
import { FileText, FolderOpen, ChevronRight, ChevronDown, BookOpen, MessageSquare, CircleDot, ChevronsUpDown, ChevronsDownUp, Clock, Check, StickyNote, ChevronRight as BreadcrumbArrow, Bookmark, Loader2, Sparkles } from "lucide-react";
import { PdfFileRow } from "./PdfFileRow";
import { OrganHandouts } from "./OrganHandouts";
import { OrganVideos } from "./OrganVideos";
import { OrganQuizzes } from "./OrganQuizzes";
import { YouTubeSection, stripYouTubeUrls } from "./YouTubeEmbed";
import { CourseReader } from "./CourseReader";
import { MarkdownNotes, stripMarkdown } from "./MarkdownNotes";
import { SyllabusEditor } from "./SyllabusEditor";
import { ProgressRing } from "./ProgressRing";
import { motion, AnimatePresence } from "framer-motion";
import { t } from "@/lib/i18n";
import { toast } from "sonner";
import axios from "axios";

const estimateReadingTime = (text) => {
  if (!text) return 0;
  const words = text.split(/\s+/).length;
  return Math.max(1, Math.ceil(words / 200));
};

const countWords = (topic) => {
  let words = 0;
  if (topic.notes) words += topic.notes.split(/\s+/).length;
  (topic.children || []).forEach(child => {
    if (child.title) words += child.title.split(/\s+/).length;
    if (child.notes) words += child.notes.split(/\s+/).length;
  });
  return words;
};

export const OrganPage = ({ organ, organIndex, onDeleteFile, onAddNode, onDeleteNode, onRefresh, apiUrl, readPoints, onToggleRead, personalNotes, onSaveNote, lang, bookmarkedIds, onToggleBookmark, isAdmin = false, studentName = "" }) => {
  const [expandedTopics, setExpandedTopics] = useState({});
  const [allExpanded, setAllExpanded] = useState(false);
  const [notesCache, setNotesCache] = useState({});
  const [loadingNotes, setLoadingNotes] = useState({});
  const [readerPointId, setReaderPointId] = useState(null);

  // Admin: AI slide → notes (bulk)
  const [slideInfo, setSlideInfo] = useState(null);
  const [bulkJob, setBulkJob] = useState(null); // {job_id, total, done, failed, status}
  const [bulkBusy, setBulkBusy] = useState(false);

  const organNum = organIndex + 1;

  // Fetch slide availability (admin only) so we know whether AI generation is possible
  useEffect(() => {
    if (!isAdmin) return;
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${apiUrl}/admin/organs/${organNum}/slides`);
        if (alive) setSlideInfo(res.data);
      } catch (e) { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [isAdmin, apiUrl, organNum]);

  const pollBulkJob = useCallback((jobId) => {
    const iv = setInterval(async () => {
      try {
        const r = await axios.get(`${apiUrl}/admin/note-jobs/${jobId}`);
        setBulkJob(r.data);
        if (r.data.status === "done") {
          clearInterval(iv);
          setBulkBusy(false);
          toast.success(`${r.data.done} ${lang === 'en' ? 'notes generated' : 'nota dijana'}${r.data.failed ? `, ${r.data.failed} gagal` : ''}!`);
        }
      } catch (e) {
        clearInterval(iv);
        setBulkBusy(false);
      }
    }, 4000);
  }, [apiUrl, lang]);

  const handleBulkGenerate = useCallback(async () => {
    if (!window.confirm(lang === 'en'
      ? "Generate AI notes (from slides) for all empty points in this Organ? This runs in the background and saves automatically."
      : "Jana nota AI (dari slide) untuk semua point kosong dalam Organ ini? Ia berjalan di latar dan disimpan automatik.")) return;
    setBulkBusy(true);
    setBulkJob(null);
    try {
      const res = await axios.post(`${apiUrl}/admin/organs/${organNum}/generate-all-notes`);
      if (!res.data.job_id) {
        setBulkBusy(false);
        toast.info(lang === 'en' ? "No empty points to fill - all done!" : "Tiada point kosong - semua dah ada nota!");
        return;
      }
      setBulkJob({ ...res.data, done: 0 });
      pollBulkJob(res.data.job_id);
    } catch (e) {
      setBulkBusy(false);
      toast.error(e.response?.data?.detail || "Gagal memulakan penjanaan");
    }
  }, [apiUrl, organNum, lang, pollBulkJob]);

  // Lazy-load notes for a topic when expanded
  const loadNotes = useCallback(async (nodeId) => {
    if (notesCache[nodeId]) return; // Already loaded
    setLoadingNotes(prev => ({ ...prev, [nodeId]: true }));
    try {
      const res = await axios.get(`${apiUrl}/nodes/${nodeId}/content`);
      setNotesCache(prev => ({ ...prev, ...res.data.notes, [nodeId]: res.data.notes[nodeId] || '' }));
    } catch (e) {
      console.error('Failed to load notes', e);
    } finally {
      setLoadingNotes(prev => ({ ...prev, [nodeId]: false }));
    }
  }, [apiUrl, notesCache]);

  // Helper: get notes for a node (from cache or from initial data)
  const getNotes = (node) => notesCache[node.id] !== undefined ? notesCache[node.id] : (node.notes || '');

  const hasChildren = organ.children && organ.children.length > 0;
  const hasFiles = organ.files && organ.files.length > 0;

  // Total reading time
  const totalReadTime = useMemo(() => {
    let total = 0;
    (organ.children || []).forEach(topic => {
      total += Math.max(1, Math.ceil(countWords(topic) / 200));
    });
    return total;
  }, [organ.children]);

  // Read stats
  const readStats = useMemo(() => {
    let totalPoints = 0;
    let readCount = 0;
    (organ.children || []).forEach(topic => {
      (topic.children || []).forEach(point => {
        totalPoints++;
        if (readPoints && readPoints[point.id]) readCount++;
      });
    });
    return { total: totalPoints, read: readCount, percent: totalPoints > 0 ? Math.round((readCount / totalPoints) * 100) : 0 };
  }, [organ.children, readPoints]);

  const toggleTopic = (topicId) => {
    const isExpanding = !expandedTopics[topicId];
    setExpandedTopics(prev => ({ ...prev, [topicId]: !prev[topicId] }));
    if (isExpanding) loadNotes(topicId);
  };

  const toggleAllTopics = () => {
    const newState = !allExpanded;
    const map = {};
    (organ.children || []).forEach(child => {
      map[child.id] = newState;
      if (newState) loadNotes(child.id);
    });
    setExpandedTopics(map);
    setAllExpanded(newState);
  };

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-gray-400" data-testid="breadcrumb">
        <button onClick={() => {}} className="hover:text-blue-500 transition-colors duration-100">Dashboard</button>
        <ChevronRight size={14} className="text-gray-300" />
        <span className="text-gray-700 font-medium">Organ {organNum}</span>
      </nav>

      {/* Organ Header Card */}
      <div className="dash-card">
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#F7F7F7] flex items-center justify-center">
              <FolderOpen size={24} className="text-[#0A0A0A]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="section-tag">Organ {organNum}</span>
                {totalReadTime > 0 && (
                  <span className="flex items-center gap-1 text-xs text-gray-400">
                    <Clock size={12} /> ~{totalReadTime} {t('minRead', lang)}
                  </span>
                )}
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900" style={{ fontFamily: 'Inter, sans-serif' }}>
                {organ.title}
              </h2>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <p className="text-sm text-gray-400">
                  {hasChildren ? `${organ.children.length} sub-topik` : 'Tiada sub-topik'}
                  {hasFiles ? ` · ${organ.files.length} PDF` : ''}
                </p>
              </div>
              {/* CTA row */}
              {hasChildren && (
                <div className="mt-4 flex items-center gap-3 flex-wrap">
                  <button
                    onClick={() => {
                      // Open reader at first unread point, else first point
                      const flat = [];
                      const walk = (nodes) => {
                        for (const n of nodes || []) {
                          const cn = n.children || [];
                          if (cn.length === 0) flat.push(n.id);
                          else walk(cn);
                        }
                      };
                      walk(organ.children || []);
                      const firstUnread = flat.find((id) => !(readPoints && readPoints[id])) || flat[0] || null;
                      setReaderPointId(firstUnread);
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-[#1a1a1a] transition-colors shadow-sm"
                    data-testid="open-reader-btn"
                  >
                    <BookOpen size={15} />
                    {readStats.read > 0
                      ? (lang === 'en' ? 'Continue Reading' : 'Sambung Baca')
                      : (lang === 'en' ? 'Start Reading' : 'Mula Baca')}
                  </button>
                  {readStats.total > 0 && (
                    <div className="flex items-center gap-2">
                      <ProgressRing
                        value={readStats.percent}
                        size={44}
                        stroke={4}
                        color="#2563EB"
                        label={`${readStats.percent}%`}
                        testId="organ-progress-ring"
                      />
                      <span className="text-xs text-gray-500 font-medium">
                        {readStats.read}/{readStats.total} {lang === 'en' ? 'read' : 'dibaca'}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Admin: AI Slide → Notes (bulk) */}
              {isAdmin && (
                <div className="mt-4 p-3.5 rounded-xl bg-[#F0F7FF] border border-[#DBEAFE]" data-testid="ai-notes-panel">
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="flex-1 min-w-[180px]">
                      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#2563EB]">
                        <Sparkles size={13} /> {lang === 'en' ? 'AI Content Generator' : 'Penjana Kandungan AI'}
                      </div>
                      <p className="text-xs text-[#475569] mt-1 leading-relaxed">
                        {slideInfo?.available
                          ? (lang === 'en'
                              ? `Slides ready (${slideInfo.slide_count} slides). Auto-write notes for every empty point in this Organ, grounded on the slide deck.`
                              : `Slide sedia (${slideInfo.slide_count} slide). Auto-tulis nota untuk setiap point kosong dalam Organ ini, berpandukan slide.`)
                          : (lang === 'en'
                              ? 'No slide deck found for this Organ yet. Upload slides to enable AI generation.'
                              : 'Tiada slide untuk Organ ini lagi. Muat naik slide untuk aktifkan penjanaan AI.')}
                      </p>
                      {bulkJob && bulkBusy && (
                        <div className="mt-2.5">
                          <div className="flex items-center justify-between text-[11px] text-[#475569] mb-1">
                            <span>{lang === 'en' ? 'Generating…' : 'Menjana…'}</span>
                            <span className="font-semibold">{bulkJob.done || 0}/{bulkJob.total || 0}</span>
                          </div>
                          <div className="h-1.5 bg-white rounded-full overflow-hidden">
                            <div className="h-full bg-[#2563EB] transition-all duration-500" style={{ width: `${bulkJob.total ? ((bulkJob.done || 0) / bulkJob.total) * 100 : 0}%` }} />
                          </div>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={handleBulkGenerate}
                      disabled={bulkBusy || !slideInfo?.available}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#2563EB] text-white text-xs font-bold hover:bg-[#1d4ed8] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm whitespace-nowrap"
                      data-testid="bulk-generate-notes-btn"
                    >
                      {bulkBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                      {bulkBusy
                        ? (lang === 'en' ? 'Generating…' : 'Menjana…')
                        : (lang === 'en' ? 'Fill Empty Points (AI)' : 'Isi Point Kosong (AI)')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Handouts (Bahan Bacaan - PDFs) */}
      <OrganHandouts apiUrl={apiUrl} organIndex={organNum} lang={lang} isAdmin={isAdmin} />

      {/* Videos (YouTube/Vimeo) */}
      <OrganVideos apiUrl={apiUrl} organIndex={organNum} lang={lang} isAdmin={isAdmin} />

      {/* Sub-topics */}
      <div className="dash-card">
        <div className="dash-card-header">
          <h3>
            <BookOpen size={16} className="text-indigo-500" />
            {t('subTopics', lang)}
          </h3>
          <div className="flex items-center gap-2">
            {hasChildren && (
              <button
                onClick={toggleAllTopics}
                className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-blue-500 px-2 py-1 rounded-md hover:bg-blue-50 transition-colors duration-100"
                data-testid="toggle-all-topics"
              >
                {allExpanded ? <ChevronsDownUp size={14} /> : <ChevronsUpDown size={14} />}
                {allExpanded ? t('collapseAll', lang) : t('expandAll', lang)}
              </button>
            )}
          </div>
        </div>
        <div className="dash-card-body">
          {hasChildren ? (
            <div className="space-y-2">
              {organ.children.map(child => (
                <TopicCard
                  key={child.id}
                  topic={child}
                  expanded={expandedTopics[child.id] || false}
                  onToggle={() => toggleTopic(child.id)}
                  onDeleteFile={onDeleteFile}
                  onDeleteNode={onDeleteNode}
                  apiUrl={apiUrl}
                  readPoints={readPoints}
                  onToggleRead={onToggleRead}
                  personalNotes={personalNotes}
                  onSaveNote={onSaveNote}
                  bookmarkedIds={bookmarkedIds}
                  onToggleBookmark={onToggleBookmark}
                  organTitle={organ.title}
                  organTree={organ}
                  getNotes={getNotes}
                  loadingNotes={loadingNotes}
                  lang={lang}
                  isAdmin={isAdmin}
                  onRefresh={onRefresh}
                />
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <BookOpen size={16} className="inline mr-2 text-gray-300" />
              Tiada sub-topik lagi.
            </div>
          )}
        </div>
      </div>

      {/* Quiz per Organ - at bottom, after student finishes studying */}
      <OrganQuizzes apiUrl={apiUrl} organ={organ} organIndex={organNum} studentName={studentName} lang={lang} isAdmin={isAdmin} />

      {/* Immersive Course Reader */}
      {readerPointId && (
        <CourseReader
          organ={organ}
          organTitle={organ.title}
          startPointId={readerPointId}
          readPoints={readPoints}
          bookmarkedIds={bookmarkedIds}
          personalNotes={personalNotes}
          onClose={() => setReaderPointId(null)}
          onToggleRead={onToggleRead}
          onToggleBookmark={onToggleBookmark}
          onSaveNote={onSaveNote}
          lang={lang}
          apiUrl={apiUrl}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
};

function TopicCard({ topic, expanded, onToggle, onDeleteFile, onDeleteNode, apiUrl, readPoints, onToggleRead, personalNotes, onSaveNote, bookmarkedIds, onToggleBookmark, organTitle, organTree, getNotes, loadingNotes, lang, isAdmin = false, onRefresh }) {
  const hasFiles = topic.files && topic.files.length > 0;
  const hasChildren = topic.children && topic.children.length > 0;
  const topicNotes = getNotes ? getNotes(topic) : (topic.notes || '');
  const hasNotes = topicNotes && topicNotes.trim();
  const hasContent = hasFiles || hasChildren || hasNotes;

  const readTime = Math.max(1, Math.ceil(countWords(topic) / 200));

  // Read count for this topic
  const childCount = (topic.children || []).length;
  const readCount = (topic.children || []).filter(c => readPoints && readPoints[c.id]).length;

  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden bg-white">
      {/* Topic header */}
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 group transition-colors duration-100"
        onClick={onToggle}
        data-testid="subtopic-toggle"
      >
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          {hasContent ? (
            <motion.div animate={{ rotate: expanded ? 90 : 0 }} transition={{ duration: 0.15 }}>
              <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
            </motion.div>
          ) : (
            <span className="w-4 flex-shrink-0" />
          )}
          <div className="w-2 h-2 rounded-full bg-indigo-400 flex-shrink-0" />
          <span className="text-sm font-semibold text-gray-800 truncate">{topic.title}</span>
          {hasChildren && (
            <span className="text-xs text-gray-400 ml-1 flex-shrink-0">
              {readCount > 0 ? `${readCount}/` : ''}{childCount} point
            </span>
          )}
          {hasFiles && <span className="size-badge flex-shrink-0">{topic.files.length} PDF</span>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isAdmin && (
            <SyllabusEditor node={topic} organTree={organTree} apiUrl={apiUrl} onChanged={onRefresh} lang={lang} />
          )}
          <span className="text-[10px] text-gray-300 flex items-center gap-0.5">
            <Clock size={10} /> {readTime}m
          </span>
        </div>
      </div>

      {/* Expanded content with animation */}
      <AnimatePresence initial={false}>
        {expanded && hasContent && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            style={{ overflow: 'hidden' }}
          >
            <div className="px-4 pb-3 border-t border-gray-50">
              {/* Loading indicator */}
              {loadingNotes && loadingNotes[topic.id] && (
                <div className="mt-3 flex items-center gap-2 text-xs text-gray-400"><Loader2 size={12} className="animate-spin" /> Loading...</div>
              )}

              {/* Notes */}
              {hasNotes && (
                <div className="mt-3">
                  <YouTubeSection notes={topicNotes} lang={lang} />
                  {stripYouTubeUrls(topicNotes).trim() && (
                    <div className="p-3 rounded-lg bg-indigo-50/50 border border-indigo-100">
                      <div className="flex items-start gap-2">
                        <MessageSquare size={14} className="text-indigo-400 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-gray-600 leading-relaxed flex-1 min-w-0">
                          <MarkdownNotes text={stripYouTubeUrls(topicNotes)} compact />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Child points */}
              {hasChildren && (
                <div className="mt-3 space-y-1">
                  {topic.children.map(child => (
                    <PointItem
                      key={child.id}
                      point={child}
                      isRead={readPoints && readPoints[child.id]}
                      onToggleRead={onToggleRead}
                      personalNote={personalNotes && personalNotes[child.id]}
                      onSaveNote={onSaveNote}
                      isBookmarked={bookmarkedIds && bookmarkedIds[child.id]}
                      onToggleBookmark={onToggleBookmark}
                      topicTitle={topic.title}
                      organTitle={organTitle}
                      getNotes={getNotes}
                      readPoints={readPoints}
                      personalNotes={personalNotes}
                      bookmarkedIds={bookmarkedIds}
                      depth={0}
                      isAdmin={isAdmin}
                      organTree={organTree}
                      apiUrl={apiUrl}
                      onRefresh={onRefresh}
                      lang={lang}
                    />
                  ))}
                </div>
              )}

              {/* Files */}
              {hasFiles && (
                <div className="mt-3 space-y-0.5">
                  {topic.files.map(file => (
                    <PdfFileRow key={file.id} file={file} onDelete={onDeleteFile} apiUrl={apiUrl} />
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function PointItem({ point, isRead, onToggleRead, personalNote, onSaveNote, isBookmarked, onToggleBookmark, topicTitle, organTitle, getNotes, readPoints, personalNotes, bookmarkedIds, depth = 0, isAdmin = false, organTree, apiUrl, onRefresh, lang = "ms" }) {
  const [showNotes, setShowNotes] = useState(false);
  const [showChildren, setShowChildren] = useState(true);
  const [editingNote, setEditingNote] = useState(false);
  const [noteText, setNoteText] = useState(personalNote || '');
  const pointNotes = getNotes ? getNotes(point) : (point.notes || '');
  const hasNotes = pointNotes && pointNotes.trim();
  const hasChildren = point.children && point.children.length > 0;
  const previewText = hasNotes ? (() => {
    const plain = stripMarkdown(stripYouTubeUrls(pointNotes));
    return plain.length > 90 ? plain.slice(0, 90) + '...' : plain;
  })() : '';
  const childCount = (point.children || []).length;
  const readChildCount = (point.children || []).filter(c => readPoints && readPoints[c.id]).length;

  const handleSaveNote = () => {
    if (onSaveNote) onSaveNote(point.id, noteText);
    setEditingNote(false);
  };

  // Visual depth marker: subtle left border for nested levels (depth > 0)
  const indentClass = depth === 0 ? 'ml-4' : 'ml-3 border-l border-gray-100 pl-3';

  return (
    <div className={indentClass}>
      <div
        className={`group flex items-start gap-2.5 px-3 py-2.5 rounded-lg transition-colors duration-100 ${(hasNotes || hasChildren) ? 'cursor-pointer hover:bg-gray-50' : ''} ${isRead ? 'bg-green-50/50' : ''}`}
        onClick={() => {
          if (hasChildren) setShowChildren(!showChildren);
          else if (hasNotes) setShowNotes(!showNotes);
        }}
      >
        {/* Read toggle (only for leaf nodes) */}
        {!hasChildren ? (
          <button
            onClick={(e) => { e.stopPropagation(); if (onToggleRead) onToggleRead(point.id); }}
            className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-150 ${isRead ? 'bg-green-500 border-green-500' : 'border-gray-300 hover:border-green-400'}`}
            data-testid="mark-read-button"
            title={isRead ? 'Tandakan belum dibaca' : 'Tandakan sudah dibaca'}
          >
            {isRead && <Check size={12} className="text-white" />}
          </button>
        ) : (
          /* Chevron indicator for parent nodes */
          <button
            onClick={(e) => { e.stopPropagation(); setShowChildren(!showChildren); }}
            className="mt-0.5 flex-shrink-0 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors"
            aria-label={showChildren ? 'Tutup' : 'Buka'}
          >
            {showChildren ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        )}

        <div className="flex-1 min-w-0">
          <span className={`text-sm font-medium ${isRead && !hasChildren ? 'text-gray-500 line-through decoration-gray-300' : 'text-gray-700'} ${hasChildren ? 'font-semibold' : ''}`}>{point.title}</span>
          {hasChildren && (
            <p className="text-[10px] text-gray-400 mt-0.5">{readChildCount}/{childCount} {childCount === 1 ? 'item' : 'items'}</p>
          )}
          {hasNotes && !showNotes && !hasChildren && (
            <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{previewText}</p>
          )}
        </div>

        {personalNote && !hasChildren && (
          <StickyNote size={12} className="text-amber-400 flex-shrink-0 mt-1" />
        )}

        {isAdmin && (
          <SyllabusEditor node={point} organTree={organTree} apiUrl={apiUrl} onChanged={onRefresh} lang={lang} compact />
        )}

        <button
          onClick={(e) => { e.stopPropagation(); if (onToggleBookmark) onToggleBookmark(point.id, point.title, pointNotes, topicTitle || '', organTitle || ''); }}
          className={`flex-shrink-0 mt-0.5 p-1 rounded transition-colors duration-100 ${isBookmarked ? 'text-[#0A0A0A]' : 'text-gray-300 hover:text-amber-400'}`}
          data-testid="bookmark-toggle"
          title={isBookmarked ? 'Buang bookmark' : 'Bookmark'}
        >
          <Bookmark size={14} fill={isBookmarked ? 'currentColor' : 'none'} />
        </button>
      </div>

      {/* Nested children - recursive via React.createElement to bypass babel visual-edits AST recursion */}
      <AnimatePresence initial={false}>
        {hasChildren && showChildren && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            style={{ overflow: 'hidden' }}
          >
            <div className="space-y-0.5 mt-1 mb-1">
              {point.children.map(child => React.createElement(PointItem, {
                key: child.id,
                point: child,
                isRead: readPoints && readPoints[child.id],
                onToggleRead,
                personalNote: personalNotes && personalNotes[child.id],
                onSaveNote,
                isBookmarked: bookmarkedIds && bookmarkedIds[child.id],
                onToggleBookmark,
                topicTitle: point.title,
                organTitle,
                getNotes,
                readPoints,
                personalNotes,
                bookmarkedIds,
                depth: depth + 1,
                isAdmin,
                organTree,
                apiUrl,
                onRefresh,
                lang,
              }))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Expanded notes (leaf nodes only) */}
      <AnimatePresence initial={false}>
        {showNotes && hasNotes && !hasChildren && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            style={{ overflow: 'hidden' }}
          >
            <div className="ml-8 mt-1 mb-2">
              <YouTubeSection notes={pointNotes} lang={'ms'} />
              {stripYouTubeUrls(pointNotes).trim() && (
                <div className="p-3 rounded-lg bg-blue-50/50 border border-blue-100">
                  <div className="text-xs text-gray-600 leading-relaxed">
                    <MarkdownNotes text={stripYouTubeUrls(pointNotes)} compact />
                  </div>
                </div>
              )}

              <div className="mt-2">
                {editingNote ? (
                  <div className="space-y-2">
                    <textarea
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="Tulis nota peribadi anda..."
                      className="w-full px-3 py-2 text-xs rounded-lg border border-amber-200 bg-[#F7F7F7]/50 focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none"
                      rows={3}
                      autoFocus
                      data-testid="personal-note-input"
                    />
                    <div className="flex gap-2">
                      <button onClick={handleSaveNote} className="text-xs px-3 py-1.5 rounded-lg bg-[#F7F7F7]0 text-white font-medium hover:bg-amber-600" data-testid="save-note-button">Simpan</button>
                      <button onClick={() => setEditingNote(false)} className="text-xs px-3 py-1.5 rounded-lg text-gray-500 hover:bg-gray-100">Batal</button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditingNote(true); setNoteText(personalNote || ''); }}
                    className="flex items-center gap-1 text-xs text-[#0A0A0A] hover:text-amber-600 font-medium transition-colors duration-100"
                    data-testid="add-note-button"
                  >
                    <StickyNote size={12} />
                    {personalNote ? 'Edit nota' : 'Tambah nota peribadi'}
                  </button>
                )}
                {personalNote && !editingNote && (
                  <div className="mt-1.5 p-2.5 rounded-lg bg-[#F7F7F7]/70 border border-amber-200">
                    <p className="text-xs text-amber-800 leading-relaxed">{personalNote}</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
